'use client';

import { useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Loader2 } from 'lucide-react';
import { isSuperAdminAccount } from '@/lib/auth/is-super-admin';

interface User {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  user_code: string | null;
  account_type: string;
  is_active: boolean;
  admin?: {
    is_super_admin: boolean;
    group_id: number;
    account_id: number;
  };
  user?: {
    id: number;
    user_type: string;
    group_id: number;
    account_id: number;
    department?: {
      id: number;
      name: string;
    };
    section_leader?: unknown;
    is_pr_creation_enabled: boolean;
    is_direct_po_enabled: boolean;
  };
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  permissions: any[];
  isLoading: boolean;
  isRedirecting: boolean;
  error: string | null;
}

interface AuthWrapperProps {
  children: React.ReactNode;
}

// Global auth state
let globalAuthState: AuthState = {
  isAuthenticated: false,
  user: null,
  permissions: [],
  isLoading: true,
  isRedirecting: false,
  error: null
};

let authCheckPromise: Promise<AuthState> | null = null;
const AUTH_STATE_UPDATED = 'auth-state-updated';

function applyCurrentPermissions(permissions: any[]) {
  globalAuthState = { ...globalAuthState, permissions };
  try {
    localStorage.setItem('auth-permissions', JSON.stringify(permissions));
    if (globalAuthState.user) {
      localStorage.setItem(
        'auth-permissions-account-id',
        String(globalAuthState.user.id)
      );
    }
  } catch {
    // Storage can be disabled; in-memory permissions remain current.
  }
  window.dispatchEvent(new Event(AUTH_STATE_UPDATED));
}

// Single auth check function that only runs once
async function checkAuthOnce(): Promise<AuthState> {
  if (authCheckPromise) {
    return authCheckPromise;
  }

  authCheckPromise = (async () => {
    try {
      const response = await fetch('/api/auth', {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store'
      });

      if (response.ok) {
        const data = await response.json();

        // Prefer permissions the server re-hydrated from the user-permissions
        // cookie (survives a hard refresh that cleared localStorage); fall back
        // to localStorage['auth-permissions'] (written at login). Whichever
        // wins is re-synced to localStorage so both stay consistent.
        let permissions: any[] = [];
        if (Array.isArray(data.permissions)) {
          permissions = data.permissions;
          try {
            localStorage.setItem(
              'auth-permissions',
              JSON.stringify(permissions)
            );
            localStorage.setItem(
              'auth-permissions-account-id',
              String(data.user.id)
            );
          } catch (_e) {
            // ignore storage errors
          }
        } else {
          try {
            const storedPerms = localStorage.getItem('auth-permissions');
            if (
              storedPerms &&
              localStorage.getItem('auth-permissions-account-id') ===
                String(data.user.id)
            ) {
              permissions = JSON.parse(storedPerms);
            }
          } catch (_e) {
            // ignore parsing errors
          }
        }

        const authState: AuthState = {
          isAuthenticated: true,
          user: data.user,
          permissions,
          isLoading: false,
          isRedirecting: false,
          error: null
        };

        globalAuthState = authState;
        return authState;
      } else {
        const authState: AuthState = {
          isAuthenticated: false,
          user: null,
          permissions: [],
          isLoading: false,
          isRedirecting: false,
          error: 'Authentication failed'
        };

        globalAuthState = authState;
        return authState;
      }
    } catch (_error) {
      const authState: AuthState = {
        isAuthenticated: false,
        user: null,
        permissions: [],
        isLoading: false,
        isRedirecting: false,
        error: 'Network error'
      };

      globalAuthState = authState;
      return authState;
    }
  })();

  return authCheckPromise;
}

// Force sign-out: clears server-side cookies, resets local state, redirects.
// The server no longer auto-clears cookies on a single validation 401 (so a
// transient backend hiccup doesn't lock everyone out); this client is the
// authority on "session is definitely dead", so it's the client's job to
// tell the server to clear the cookies via DELETE /api/auth.
async function forceSignOut() {
  authCheckPromise = null;
  globalAuthState = {
    isAuthenticated: false,
    user: null,
    permissions: [],
    isLoading: false,
    isRedirecting: false,
    error: null
  };
  try {
    localStorage.removeItem('auth-permissions');
    localStorage.removeItem('auth-permissions-account-id');
  } catch {
    // localStorage may be unavailable in restricted browser contexts.
  }
  try {
    await fetch('/api/auth', { method: 'DELETE', credentials: 'include' });
  } catch {
    // If we can't reach the server to clear cookies, redirect anyway —
    // the user is on a dead session either way.
  }
  window.location.href = '/login?reason=session-expired';
}

// Re-validates the session against the server. Tolerates a single transient
// 401 — only a second consecutive 401 (5s apart) is treated as a confirmed
// dead session. This protects against momentary backend hiccups or 401s
// caused by reasons other than a dead token (rate limits, endpoint-specific
// permission anomalies, brief deploy windows).
async function revalidateSession(): Promise<boolean> {
  const hit = async () => {
    try {
      const response = await fetch('/api/auth', {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store'
      });
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data.permissions)) {
          applyCurrentPermissions(data.permissions);
        }
      }
      return { ok: response.ok, status: response.status };
    } catch {
      return { ok: false, status: 0 }; // network error
    }
  };

  const first = await hit();
  if (first.ok) return true;
  if (first.status !== 401) return true; // 5xx or network — trust the cookie

  // First 401 — give the backend a chance to recover and try once more.
  await new Promise((r) => setTimeout(r, 5000));
  const second = await hit();
  if (second.ok) return true;
  if (second.status !== 401) return true; // recovered to a non-401 state

  return false; // two consecutive 401s — session is genuinely dead
}

export default function AuthWrapper({ children }: AuthWrapperProps) {
  const [authState, setAuthState] = useState<AuthState>(globalAuthState);
  const pathname = usePathname();
  const router = useRouter();
  const isDeviceDisplayRoute =
    pathname === '/tv' || /^\/(?:en|ar)\/tv$/.test(pathname);

  useEffect(() => {
    const sync = () => setAuthState(globalAuthState);
    window.addEventListener(AUTH_STATE_UPDATED, sync);
    return () => window.removeEventListener(AUTH_STATE_UPDATED, sync);
  }, []);

  useEffect(() => {
    if (isDeviceDisplayRoute) return;
    // Only run auth check once globally
    if (!authCheckPromise) {
      checkAuthOnce().then(setAuthState);
    } else {
      // If already checking, just wait for it
      authCheckPromise.then(setAuthState);
    }
  }, [isDeviceDisplayRoute]);

  // Detect session expiry while the user is on the page:
  // 1. When the user returns to the tab after being away
  // 2. Every 90 seconds as a safety net for users who stay on the page
  //    (was 30 min, which left an expired session usable-looking for too long)
  useEffect(() => {
    if (isDeviceDisplayRoute || !authState.isAuthenticated) return;

    const check = async () => {
      const valid = await revalidateSession();
      if (!valid) forceSignOut();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    const interval = setInterval(check, 90 * 1000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, [authState.isAuthenticated, isDeviceDisplayRoute]);

  useEffect(() => {
    // Handle routing after auth is determined
    if (
      !isDeviceDisplayRoute &&
      !authState.isLoading &&
      !authState.isRedirecting
    ) {
      const isProtectedRoute =
        pathname.startsWith('/dashboard') || pathname.includes('/dashboard');
      const isLoginRoute = pathname.includes('/login');

      if (!authState.isAuthenticated && isProtectedRoute) {
        // Use a small delay or window.setTimeout to avoid synchronous state update in effect
        setTimeout(() => {
          setAuthState((prev) => ({ ...prev, isRedirecting: true }));
          const locale = pathname.split('/')[1] || 'en';
          const loginPath = ['en', 'ar'].includes(locale)
            ? `/${locale}/login`
            : '/login';
          router.replace(loginPath);
        }, 0);
      } else if (authState.isAuthenticated && isLoginRoute) {
        setTimeout(() => {
          setAuthState((prev) => ({ ...prev, isRedirecting: true }));
          const locale = pathname.split('/')[1] || 'en';
          const dashboardPath = ['en', 'ar'].includes(locale)
            ? `/${locale}/dashboard/overview`
            : '/dashboard/overview';
          router.replace(dashboardPath);
        }, 0);
      }
    }
  }, [
    authState.isLoading,
    authState.isAuthenticated,
    authState.isRedirecting,
    pathname,
    router,
    isDeviceDisplayRoute
  ]);

  if (isDeviceDisplayRoute) return <>{children}</>;

  // Show loading page while checking auth or redirecting
  if (authState.isLoading || authState.isRedirecting) {
    const loadingMessage = authState.isLoading
      ? 'Loading...'
      : 'Redirecting to dashboard...';

    const loadingSubtext = authState.isLoading
      ? 'Loading'
      : 'Taking you to your dashboard';

    return (
      <div className='bg-background fixed inset-0 z-50 flex items-center justify-center'>
        <div className='flex flex-col items-center space-y-4'>
          <div className='relative'>
            <Loader2 className='text-primary h-12 w-12 animate-spin' />
            <div className='border-primary/20 border-t-primary absolute inset-0 h-12 w-12 animate-spin rounded-full border-2'></div>
          </div>
          <div className='text-center'>
            <h2 className='text-foreground text-lg font-semibold'>
              {loadingMessage}
            </h2>
            <p className='text-muted-foreground text-sm'>{loadingSubtext}</p>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

// Export a hook to use the auth state
export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>(globalAuthState);

  useEffect(() => {
    const sync = () => setAuthState(globalAuthState);
    window.addEventListener(AUTH_STATE_UPDATED, sync);
    if (authCheckPromise) {
      authCheckPromise.then(setAuthState);
    }
    return () => window.removeEventListener(AUTH_STATE_UPDATED, sync);
  }, []);

  // Super Admin access is based on the account contract, not on the
  // potentially incomplete admin permission list.
  const isSuperAdmin = isSuperAdminAccount(authState.user);

  const hasPermission = (httpMethod: string, modelName: string) => {
    if (isSuperAdmin) return true;
    return authState.permissions.some(
      (permission) =>
        permission.http_method === httpMethod &&
        permission.model_name === modelName
    );
  };

  const hasPermissionByTitle = (title: string) => {
    if (isSuperAdmin) return true;
    return authState.permissions.some(
      (permission) => permission.title === title
    );
  };

  const hasPermissionByActionName = (actionName: string) => {
    if (isSuperAdmin) return true;
    return authState.permissions.some(
      (permission) => permission.action_name === actionName
    );
  };

  const hasAnyPermission = (
    permissions: { httpMethod: string; modelName: string }[]
  ) => {
    return permissions.some(({ httpMethod, modelName }) =>
      hasPermission(httpMethod, modelName)
    );
  };

  const hasAllPermissions = (
    permissions: { httpMethod: string; modelName: string }[]
  ) => {
    return permissions.every(({ httpMethod, modelName }) =>
      hasPermission(httpMethod, modelName)
    );
  };

  return {
    ...authState,
    isSuperAdmin,
    hasPermission,
    hasPermissionByTitle,
    hasPermissionByActionName,
    hasAnyPermission,
    hasAllPermissions
  };
}

// Export logout function
export async function logout() {
  // Best-effort: unregister FCM push token before clearing session.
  // Dynamic import so firebase bundle is not loaded unless push was used.
  try {
    const { unregisterWebPush } = await import('@/lib/push/web-push');
    await unregisterWebPush();
  } catch {
    // best-effort — never block logout
  }

  try {
    await fetch('/api/auth', {
      method: 'DELETE',
      credentials: 'include'
    });
  } catch (_error) {
    // Ignore logout error as we are redirecting anyway
  } finally {
    // Reset global state
    globalAuthState = {
      isAuthenticated: false,
      user: null,
      permissions: [],
      isLoading: false,
      isRedirecting: false,
      error: null
    };
    authCheckPromise = null;

    try {
      localStorage.removeItem('auth-permissions');
      localStorage.removeItem('auth-permissions-account-id');
    } catch (_e) {
      // Ignore exception if localStorage is blocked
    }

    // Redirect to login
    // Redirect to login using window.location for full reload
    const locale = window.location.pathname.split('/')[1] || 'en';
    window.location.href = `/${locale}/login`;
  }
}
